import React, { useEffect, useRef, useState } from "react";
import { Send, X, Sparkles, Loader2 } from "lucide-react";
import "./ZyftPandaChatbot.css";

export default function ZyftPandaChatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi, I’m your Zyft Panda. How can I help you today?"
    }
  ]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  const sendMessage = async () => {
    const userMessage = input.trim();
    if (!userMessage || loading) return;

    const nextMessages = [...messages, { role: "user", text: userMessage }];

    setMessages(nextMessages);
    setInput("");
    setLoading(true);

    try {
      const response = await fetch("/api/panda-chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          message: userMessage,
          currentPath: window.location.pathname,
          history: nextMessages
        })
      });

      const data = await response.json();

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            data.answer ||
            "I’m here, but I could not generate an answer right now."
        }
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            "I’m having trouble connecting to the backend. Please check that the Flask backend is running on port 5000."
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    "How do I use AI Gifting?",
    "Why can’t I login?",
    "What is Wishes & Badges?",
    "How do I add employees?"
  ];

  return (
    <div className="zyftPandaRoot">
      {open && (
        <div className="zyftPandaPanel">
          <div className="zyftPandaHeader">
            <div className="zyftPandaHeaderLeft">
              <div className="zyftPandaMiniAvatar">
                <span>🐼</span>
              </div>

              <div>
                <h3>Zyft Panda</h3>
                <p>App guide and support assistant</p>
              </div>
            </div>

            <button
              type="button"
              className="zyftPandaClose"
              onClick={() => setOpen(false)}
            >
              <X size={18} />
            </button>
          </div>

          <div className="zyftPandaWelcome">
            <Sparkles size={16} />
            <span>Ask me anything about using Zyft.AI.</span>
          </div>

          <div className="zyftPandaMessages">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`zyftPandaMessage ${
                  message.role === "user" ? "user" : "assistant"
                }`}
              >
                {message.role === "assistant" && (
                  <div className="zyftPandaBubbleAvatar">🐼</div>
                )}

                <p>{message.text}</p>
              </div>
            ))}

            {loading && (
              <div className="zyftPandaMessage assistant">
                <div className="zyftPandaBubbleAvatar">🐼</div>
                <p className="zyftPandaThinking">
                  <Loader2 size={15} />
                  Zyft Panda is thinking...
                </p>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="zyftPandaQuick">
            {quickQuestions.map((question) => (
              <button
                key={question}
                type="button"
                onClick={() => setInput(question)}
              >
                {question}
              </button>
            ))}
          </div>

          <div className="zyftPandaInputArea">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message Zyft Panda..."
              rows={1}
            />

            <button
              type="button"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
            >
              <Send size={17} />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`zyftPandaLauncher ${open ? "open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Open Zyft Panda chatbot"
      >
        <div className="zyftPandaFace">
          <span className="pandaEar pandaEarLeft" />
          <span className="pandaEar pandaEarRight" />
          <span className="pandaEye pandaEyeLeft" />
          <span className="pandaEye pandaEyeRight" />
          <span className="pandaNose" />
          <span className="pandaSmile" />
        </div>

        {!open && (
          <div className="zyftPandaGreeting">
            Hi, I’m your Zyft Panda. How can I help?
          </div>
        )}
      </button>
    </div>
  );
}